/* eslint-disable react/prop-types */
import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  ForwardToInbox as ForwardToInboxIcon,
} from "@mui/icons-material";
import StepCheck from "./observationStep";
import useGetMetadata from "@/hooks/useGetMetaData";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { useSession } from "@/hooks/useSession";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import axios from "@/api/axios";

// Import sub-components
import FileViewerDialog from "./components/FileViewerDialog";
import ObservationDetailsCard from "./components/ObservationDetailsCard";
import AttachmentsSection from "./components/AttachmentsSection";
import TimelineSection from "./components/TimelineSection";

// Main ObservationDetailsDialog Component
const ObservationDetailsDialog = ({
  observation,
  onClose,
  onActionSuccess,
}) => {
  const { t } = useTranslation();
  const { UsersList } = useGetMetadata();
  const theme = useTheme();
  const { session, token } = useSession();
  const { userDetails } = useUsersSettingService(session?.id);
  const [openStepDialog, setOpenStepDialog] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [augmentedUsersList, setAugmentedUsersList] = useState(UsersList || []);

  // Fetch missing users from step inputs and augment UsersList
  useEffect(() => {
    const fetchMissingUsers = async () => {
      if (
        !observation?.history_steps ||
        !Array.isArray(observation.history_steps)
      ) {
        return;
      }

      // Extract all user IDs from step_input_values
      const userIdsInSteps = new Set();
      observation.history_steps.forEach((step) => {
        if (step.step_input_values && Array.isArray(step.step_input_values)) {
          step.step_input_values.forEach((input) => {
            if (input.input_type === "USER_SELECTION" && input.raw_value) {
              userIdsInSteps.add(input.raw_value);
            } else if (
              input.input_type === "MULTI_USER_SELECTION" &&
              Array.isArray(input.raw_value)
            ) {
              input.raw_value.forEach((userId) => userIdsInSteps.add(userId));
            }
          });
        }
      });

      // Find missing users (not in UsersList)
      const currentUsersList = UsersList || [];
      const missingUserIds = Array.from(userIdsInSteps).filter(
        (userId) => !currentUsersList.some((user) => user.value === userId)
      );

      if (missingUserIds.length === 0) {
        // No missing users, use the original UsersList
        setAugmentedUsersList(currentUsersList);
        return;
      }

      // Fetch missing users from API
      const fetchedUsers = [];
      for (const userId of missingUserIds) {
        try {
          const response = await axios.get(`/api/v1/users/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (
            response.data &&
            response.data.first_name &&
            response.data.last_name
          ) {
            fetchedUsers.push({
              value: response.data.id,
              label: `${response.data.first_name} ${response.data.last_name}`,
            });
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          // Continue with other users even if one fails
        }
      }

      // Merge fetched users with existing UsersList
      if (fetchedUsers.length > 0) {
        setAugmentedUsersList([...currentUsersList, ...fetchedUsers]);
      } else {
        setAugmentedUsersList(currentUsersList);
      }
    };

    fetchMissingUsers();
  }, [observation, UsersList, token]);

  // Handle file click
  const handleFileClick = useCallback((url) => {
    const cleanUrl = url.split("?")[0];
    const ext = cleanUrl.split(".").pop().toLowerCase();
    if (ext === "pdf") {
      setSelectedFileType("pdf");
    } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
      setSelectedFileType("office");
    } else if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp", "tiff"].includes(ext)
    ) {
      setSelectedFileType("image");
    } else {
      setSelectedFileType("other");
    }
    setSelectedFile(url);
  }, []);

  // Handle next step action
  const handleNextStepAction = useCallback((step) => {
    setSelectedStep(step);
    setOpenStepDialog(true);
  }, []);

  // Handle step action success
  const handleStepActionSuccess = useCallback(() => {
    setOpenStepDialog(false);
    setSelectedStep(null);
    onClose();
    onActionSuccess?.();
  }, [onClose, onActionSuccess]);

  // Handle share observation
  const handleShareObservation = useCallback(() => {
    // Create a shareable URL for the observation using the route /observation/:observationId
    const observationUrl = `${window.location.origin}/observation/${observation.id}`;

    // Email subject
    const subject = encodeURIComponent(
      t("observationDetails.shareEmailSubject", {
        workflow: observation.workflow?.name || "Observation",
        defaultValue: `Observation: ${observation.workflow?.name || "Observation"}`,
      })
    );

    // Email body - just the URL link
    const body = encodeURIComponent(observationUrl);

    // Open email client with pre-filled content
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [observation, t]);

  if (!observation || !userDetails) {
    return (
      <Dialog
        open
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            maxWidth: 1200,
            borderRadius: 2,
            bgcolor: theme.palette.checklist.surface.elevated,
            boxShadow: theme.shadows[10],
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.checklist.surface.subtle,
            borderBottom: 1,
            borderColor: theme.palette.checklist.border.subtle,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {t("observationDetails.noWorkflow")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>{t("observationDetails.loading")}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="observation-details-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            maxWidth: 1200,
            borderRadius: 2,
            bgcolor: theme.palette.checklist.surface.elevated,
            boxShadow: theme.shadows[10],
            transition: "all 0.3s ease-in-out",
          },
        }}
      >
        <DialogTitle
          id="observation-details-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3, md: 4 },
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: "primary.main",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Box
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: 2,
                bgcolor: (theme) => `${theme.palette.primary.main}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssignmentIcon
                sx={{
                  color: "primary.main",
                  fontSize: { xs: 20, sm: 24 },
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {observation.workflow?.name ||
                  t("observationDetails.noWorkflow")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {t(
                  "observationDetails.dialogSubtitle",
                  "Observation Details & Actions"
                )}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "action.hover",
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              "&:hover": {
                bgcolor: "action.selected",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease-in-out",
            }}
            aria-label={t("observationDetails.closeDialogAria")}
          >
            <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: "background.default",
          }}
        >
          {/* Observation Details Card */}
          <ObservationDetailsCard
            observation={observation}
            t={t}
            convertUTCToLocal={convertUTCToLocal}
            handleFileClick={handleFileClick}
          />

          {/* Attachments Section */}
          {observation.attachments && observation.attachments.length > 0 && (
            <>
              <Divider
                sx={{
                  my: 3,
                  borderColor: "divider",
                }}
              />
              <Box sx={{ mb: 4 }}>
                <AttachmentsSection
                  attachments={observation.attachments}
                  convertUTCToLocal={convertUTCToLocal}
                  t={t}
                />
              </Box>
            </>
          )}

          {/* Timeline Section */}
          <TimelineSection
            historySteps={observation.history_steps || []}
            convertUTCToLocal={convertUTCToLocal}
            onFileClick={handleFileClick}
            UsersList={augmentedUsersList}
            t={t}
          />
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Share Button */}
          <Button
            onClick={handleShareObservation}
            variant="outlined"
            color="secondary"
            startIcon={<ForwardToInboxIcon />}
            sx={{
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {t("observationDetails.shareButton", "Share")}
          </Button>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
              flex: 1,
            }}
          >
            {observation.next_steps?.length > 0 ? (
              observation.next_steps.map((step) => {
                // Extract user groups
                const stepUserGroups = step.user_groups || [];
                const userGroups = userDetails.user_groups || [];

                // Check if user has any of the required groups
                const hasMatchingUserGroup = stepUserGroups.some((stepGroup) =>
                  userGroups.some((userGroup) => userGroup.id === stepGroup.id)
                );

                // Check author condition:
                // - If self_actionable is true, author can perform the action
                // - If self_actionable is false, author cannot perform the action
                const isAuthor = observation.author?.id === userDetails.id;
                const isAuthorCheck = step.self_actionable ? true : !isAuthor;

                const disableReasons = [];
                if (!hasMatchingUserGroup) {
                  disableReasons.push(
                    t("documentDetail.noMatchingUserGroup", {
                      groups: step.user_groups.map((g) => g.name).join(", "),
                    })
                  );
                }
                if (!isAuthorCheck) {
                  disableReasons.push(t("documentDetail.authorCannotPerform"));
                }
                const isDisabled = disableReasons.length > 0;

                return (
                  <Tooltip
                    key={step.step_id}
                    title={
                      isDisabled
                        ? t("documentDetail.buttonDisabledReason", {
                            reason: disableReasons.join("; "),
                          })
                        : ""
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        onClick={() => handleNextStepAction(step)}
                        variant="contained"
                        color="primary"
                        disabled={isDisabled}
                        sx={{
                          borderRadius: 2,
                          px: { xs: 3, sm: 4 },
                          py: { xs: 1, sm: 1.5 },
                          fontWeight: 600,
                          textTransform: "none",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          minWidth: { xs: 120, sm: 140 },
                          boxShadow: 2,
                          "&:hover": {
                            boxShadow: 4,
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                        }}
                      >
                        {step.action}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {t("observationDetails.noActionsAvailable")}
              </Typography>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      {selectedFile && (
        <FileViewerDialog
          fileUrl={selectedFile}
          fileType={selectedFileType}
          onClose={() => setSelectedFile(null)}
          t={t}
        />
      )}

      {/* Step Check Dialog */}
      {selectedStep && (
        <StepCheck
          documentId={observation.id}
          isCommentDialogOpen={openStepDialog}
          setIsCommentDialogOpen={setOpenStepDialog}
          currentStepId={selectedStep.step_id}
          stepName={selectedStep.action}
          onClose={() => setOpenStepDialog(false)}
          stepInputs={selectedStep.step_inputs || []}
          onSuccess={handleStepActionSuccess}
          UsersList={augmentedUsersList}
        />
      )}
    </>
  );
};

export default ObservationDetailsDialog;
